import { useId, useState, type ChangeEvent, type ReactNode } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import type { FieldElement } from '../../format/form-definition';
import type { Validation } from '../../format/pieces';
import { useBuilder } from '../context';
import { defaultText } from '../../validations';
import { Button, Tooltip } from 'framework/ui/mui-builder';
import { FloatingLabelInputLayout } from 'framework/ui/layout';

interface RuleHeadProps {
  name: string;
  nameId?: string;
  children?: ReactNode;
}

export const RuleHead = ({ name, nameId, children }: RuleHeadProps) => {
  return (
    <Stack useFlexGap direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography id={nameId} component="span">
        {name}
      </Typography>
      {children}
    </Stack>
  );
};

interface RuleExtrasProps {
  field: FieldElement;
  validation: Validation;
}

export const RuleExtras = ({ field, validation }: RuleExtrasProps) => {
  const { form, edit } = useBuilder();
  const errorTextId = useId();
  const [errorTextOpen, setErrorTextOpen] = useState(false);
  const showErrorText = errorTextOpen || validation.message !== undefined;
  const setMessage = (message: string | undefined) => {
    edit({ kind: 'setValidation', fieldId: field.id, validation: { ...validation, message } });
  };

  const showErrorTextButton = !showErrorText;

  return (
    <Stack useFlexGap spacing={2}>
      {showErrorText && (
        <Stack useFlexGap direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          <FloatingLabelInputLayout
            source={errorTextId}
            label="Error text"
            value={validation.message ?? ''}
            sx={{ mb: 0 }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                placeholder: defaultText(form, field, validation),
                onChange: (event: ChangeEvent<HTMLInputElement>) => {
                  setErrorTextOpen(true);
                  setMessage(event.currentTarget.value);
                },
              },
            }}
          />
          <Tooltip title="Use the default text" placement="left" disableInteractive>
            <IconButton
              size="small"
              onClick={() => {
                setErrorTextOpen(false);
                setMessage(undefined);
              }}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      {showErrorTextButton && (
        <Stack useFlexGap direction="row" spacing={1}>
          <Button size="small" variant="text" startIcon={<Add />} onClick={() => setErrorTextOpen(true)}>
            Error text
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
